/**
 * What: The one renderer for server-built Study Builder sections. It draws a section through the shared frame: the header,
 *   the body, the status area and the footer the Worker sent, each a list of typed blocks (select, number, text, row,
 *   tiles, note, action, product, upload). It reports each change as `{key, value}`.
 * Transport / data-flow: Pure presentation over the payload. It owns layout, focus and accessibility only: no option
 *   lists, headings, labels, regions, phrases or field meaning. The Worker builds the blocks (`audience-sections.ts`),
 *   decides which region each is in, and decides what a reported edit means. Adding a block kind is a contract change;
 *   the exhaustive switch below fails the type-check until the new kind is drawn.
 */
import {
  isFieldBlock,
  type BuilderBlock,
  type BuilderClientComponent,
  type ClientAction,
  type ShopifyStudyBuilderEvaluationResponse,
} from '@testfeed/shopify-contracts'
import type { useShopifyStudyBuilder } from '../../hooks/study-builder'
import type { ReactNode } from 'react'
import './SectionBlocks.css'
import { ActionBlock } from './ActionBlock'
import { BlockMessage, fieldError, messageLine } from './BlockMessage'
import { CheckboxBlock } from './CheckboxBlock'
import { GroupBlock } from './GroupBlock'
import { BannerBlock, NoteBlock, ProductBlock } from './InfoBlocks'
import { RequiredField, requiredReasonOf } from './RequiredMark'
import { ReviewFieldsPane } from './ReviewFieldsPane'
import {
  FAIL_WITHOUT_CLIENT_ACTIONS,
  FAIL_WITHOUT_CLIENT_COMPONENTS,
  NO_LOCKS,
  NO_PENDING,
} from './section-block-defaults'
import { SectionFrame } from './SectionFrame'
import { TextBlock } from './TextBlock'
import { TilesBlock } from './TilesBlock'
import { UploadBlock } from './UploadBlock'
import { SuggestionFeedbackBlock } from './SuggestionFeedbackBlock'
import { WorkingCue } from './WorkingCue'

type Section = ShopifyStudyBuilderEvaluationResponse['sections'][number]
/** `typing`: more may follow, so the check waits for a pause. `now` (the default): a finished choice, checked at once. */
/** The newest value the merchant has set for each field key that the Worker has not answered yet. */
export type PendingValues = ReadonlyMap<string, string>
/** The few things only the browser can do. The Worker names one on an action block; the page supplies the handler. */
/** `arg` is the id of the tile the merchant pressed, for a tile that names a handler. */
export type ClientActions = Readonly<Record<ClientAction, (arg?: string) => void>>
/** Browser-rendered composites the Worker places by `client` block kind; the page supplies how each one looks. */
export type ClientComponents = Readonly<Record<BuilderClientComponent, () => ReactNode>>
/** What the page does not let the merchant change right now (a permission or a save in progress, not a section decision). */
export type Locks = { text: boolean; structure: boolean }
export type OnUpload = ReturnType<typeof useShopifyStudyBuilder>['handleImageUpload']
export type PendingUpload = ReturnType<typeof useShopifyStudyBuilder>['upload']
const uploadHandlerMissing: OnUpload = () => {
  throw new Error('STUDY_BUILDER_UPLOAD_HANDLER_MISSING')
}

export type OnEdit = (key: string, value: string, pace?: 'now' | 'typing') => void

type BlockProps = {
  block: BuilderBlock
  onEdit: OnEdit
  onUpload?: OnUpload
  upload?: PendingUpload
  locks: Locks
  pending: PendingValues
  clientActions: ClientActions
  clientComponents: ClientComponents
  /** The field's own label is drawn elsewhere (a group badge), so show it only to assistive tech. */
  labelHidden?: boolean
}

/**
 * A required field is marked required for assistive tech and its label is drawn with the Worker's "*" and hover text:
 * here, or by the group badge's own "*" when `labelHidden` (an option row). Every field kind then draws what the Worker says
 * about it through `BlockMessage` (a warning, or what it waits on, under the field; an error on the field itself or as a red
 * line). Every other block is drawn as sent.
 *
 * The `upload` tile is the one exception: it owns its own details column, so it draws its label and the Worker's "*" there
 * itself (`UploadBlock` reads `requiredReason`). We never wrap it in `RequiredField` or force `labelHidden`.
 */
function Block(props: BlockProps) {
  const { block, pending } = props
  const reason = requiredReasonOf(block)
  const ownsLabel = block.kind === 'upload'
  const line = isFieldBlock(block) ? messageLine(block, pending) : null
  const body = <BlockBody {...props} {...(reason && !ownsLabel ? { labelHidden: true, required: true } : {})} />
  const field = line ? (
    <s-stack direction="block" gap="small-300">
      {body}
      <BlockMessage line={line} />
    </s-stack>
  ) : (
    body
  )
  if (!reason || ownsLabel || props.labelHidden || !('label' in block) || !('key' in block)) return field
  return (
    <RequiredField markKey={block.key} label={block.label} reason={reason}>
      {field}
    </RequiredField>
  )
}

function BlockBody({
  block,
  onEdit,
  onUpload,
  upload,
  locks,
  pending,
  clientActions,
  clientComponents,
  labelHidden = false,
  required = false,
}: BlockProps & { required?: boolean }) {
  switch (block.kind) {
    case 'select':
      return (
        <s-select
          label={block.label}
          required={required}
          labelAccessibilityVisibility={labelHidden ? 'exclusive' : undefined}
          details={block.details ?? undefined}
          error={fieldError(block, pending)}
          value={pending.get(block.key) ?? block.value}
          disabled={block.disabled || locks.structure}
          onInput={(event) => onEdit(block.key, event.currentTarget.value)}
        >
          {block.options.map((option) => (
            <s-option key={option.value} value={option.value} disabled={option.disabled}>
              {option.label}
            </s-option>
          ))}
        </s-select>
      )
    case 'number':
      // Reported on commit (blur or Enter), not per keystroke: the typed text stays the merchant's until it is committed.
      return (
        <s-number-field
          label={block.label}
          required={required}
          labelAccessibilityVisibility={labelHidden ? 'exclusive' : undefined}
          error={fieldError(block, pending)}
          value={pending.get(block.key) ?? block.value}
          placeholder={block.placeholder ?? undefined}
          disabled={block.disabled || locks.structure}
          autocomplete="off"
          onChange={(event) => onEdit(block.key, event.currentTarget.value)}
        />
      )
    case 'text':
      return (
        <TextBlock
          block={block}
          onEdit={onEdit}
          readOnly={locks.text}
          value={pending.get(block.key) ?? block.value}
          error={fieldError(block, pending)}
          labelHidden={labelHidden}
          required={required}
        />
      )
    case 'row':
      return (
        <s-grid gridTemplateColumns="repeat(2, minmax(0, 1fr))" gap="base">
          {block.blocks.map((child) => (
            <Block
              key={child.key}
              block={child}
              onEdit={onEdit}
              onUpload={onUpload}
              upload={upload}
              locks={locks}
              pending={pending}
              clientActions={clientActions}
              clientComponents={clientComponents}
            />
          ))}
        </s-grid>
      )
    case 'tiles':
      return (
        <TilesBlock
          block={block}
          chosen={pending.get(block.key) ?? block.value}
          disabled={block.disabled || locks.structure}
          onEdit={onEdit}
          clientActions={clientActions}
        />
      )
    case 'field-picker':
      return <ReviewFieldsPane block={block} onEdit={onEdit} disabled={block.disabled || locks.structure} />
    case 'checkbox':
      // A ticked/unticked box reported as 'on'/'off'; pending overlays the sent state.
      return (
        <CheckboxBlock
          block={block}
          onEdit={onEdit}
          checked={pending.has(block.key) ? pending.get(block.key) === 'on' : block.checked}
          disabled={block.disabled || locks.structure}
          error={fieldError(block, pending)}
        />
      )
    case 'note':
      return <NoteBlock block={block} />
    case 'banner':
      // The banner's action (the audience Reset button) is an ordinary action block, drawn inside the banner.
      return (
        <BannerBlock
          block={block}
          action={
            block.action ? (
              <ActionBlock block={block.action} locks={locks} clientActions={clientActions} onEdit={onEdit} />
            ) : null
          }
        />
      )
    case 'divider':
      return <s-divider />
    case 'client':
      // A browser-rendered composite the Worker placed by kind: draw it from the page's registry (the summary sidebar).
      return <>{clientComponents[block.component]()}</>
    case 'product':
      return <ProductBlock block={block} />
    case 'upload':
      return (
        <UploadBlock
          block={block}
          onEdit={onEdit}
          onUpload={onUpload ?? uploadHandlerMissing}
          upload={upload ?? null}
          disabled={block.disabled || locks.structure}
          value={pending.get(block.key) ?? block.value}
          error={fieldError(block, pending)}
          labelHidden={labelHidden}
        />
      )
    case 'action':
      return <ActionBlock block={block} locks={locks} clientActions={clientActions} onEdit={onEdit} />
    case 'suggestion-feedback':
      return <SuggestionFeedbackBlock block={block} onEdit={onEdit} pending={pending} locks={locks} />
    case 'group':
      // Whether the group's own fields drop their labels to assistive-tech-only is the Worker's call (`labelChildren`), not
      // inferred from the presence of a badge. Children keep pending/locks.
      return (
        <GroupBlock block={block}>
          {block.blocks.map((child, index) => (
            <Block
              key={blockKey(child, index)}
              block={child}
              onEdit={onEdit}
              onUpload={onUpload}
              upload={upload}
              locks={locks}
              pending={pending}
              clientActions={clientActions}
              clientComponents={clientComponents}
              labelHidden={block.labelChildren}
            />
          ))}
        </GroupBlock>
      )
  }
}

function blockKey(block: BuilderBlock, index: number) {
  return 'key' in block ? block.key : `${block.kind}:${index}`
}

/**
 * `stale`: the last evaluation failed, so the status area is the previous one and is not shown as current.
 * `workingPhrase`: the Worker's word for the edit that started this check, set ONLY on the section that owns that edit
 * (`resolveWorkingCues`); null everywhere else. The owning section shows it in place of its notes; buttons stay mounted and
 * enabled, because a click that starts from a field's blur must still land. Fields and the footer are never hidden.
 */
export function SectionBlocks({
  section,
  onEdit,
  onUpload,
  upload,
  stale,
  workingPhrase = null,
  requestFailure,
  locks = NO_LOCKS,
  pending = NO_PENDING,
  clientActions = FAIL_WITHOUT_CLIENT_ACTIONS,
  clientComponents = FAIL_WITHOUT_CLIENT_COMPONENTS,
}: {
  section: Section
  onEdit: OnEdit
  onUpload?: OnUpload
  upload?: PendingUpload
  stale: boolean
  workingPhrase?: string | null
  /** Browser transport failure, rendered in the same status region as server notes. */
  requestFailure?: ReactNode
  /** Typed text is disabled by `text`; choices and buttons by `structure`. */
  locks?: Locks
  /** Edits the Worker has not answered yet, laid over the payload by field key so every field answers at once. */
  pending?: PendingValues
  /** What the browser does for an action block the Worker marks as client work (open the picker or a panel). */
  clientActions?: ClientActions
  /** How the browser draws each `client` block the Worker places by kind (the summary sidebar's composites). */
  clientComponents?: ClientComponents
}) {
  const { header } = section
  if (!header) return null
  const sectionReason = requiredReasonOf(header)
  const phrase = workingPhrase
  const draw = (blocks: BuilderBlock[]) =>
    blocks.map((block, index) => (
      <Block
        key={blockKey(block, index)}
        block={block}
        onEdit={onEdit}
        onUpload={onUpload}
        upload={upload}
        locks={locks}
        pending={pending}
        clientActions={clientActions}
        clientComponents={clientComponents}
      />
    ))
  const status = stale
    ? []
    : section.status.filter((block) => !(phrase !== null && (block.kind === 'note' || block.kind === 'banner')))
  return (
    <SectionFrame
      title={header.title}
      size={header.size}
      description={header.description}
      badge={header.badge}
      required={sectionReason ? { key: section.id, reason: sectionReason } : undefined}
      action={header.action ? draw([header.action]) : undefined}
      status={
        phrase || status.length || requestFailure ? (
          <>
            {requestFailure}
            {phrase ? <WorkingCue phrase={phrase} /> : null}
            {draw(status)}
          </>
        ) : null
      }
      footer={section.footer.length ? draw(section.footer) : null}
    >
      {draw(section.body)}
    </SectionFrame>
  )
}
